// /treasury/payouts — the admin payout review queue (SM-308).
//
// ── why this page exists ─────────────────────────────────────────────
//
// SM-308 shipped the whole escrow workflow: an admin requests a payout
// from their commission balance, the amount is held in
// SYSTEM_ADMIN_PAYOUT_ESCROW, and a reviewer releases or returns it.
// Every state transition was built and proven — but the only screen was
// the REQUESTER's (My balance → Withdrawals). A request could reach
// "Awaiting review" and then had nowhere to go, because no reviewer
// could see it. QA moved the ticket back for exactly that. This is the
// missing half.
//
// ── one queue, not a tab bar ─────────────────────────────────────────
//
// Same reasoning as the dispute queue (SM-320): buckets hide rows. The
// default view is what needs a human — requests still holding money,
// PENDING first — and the status filter widens it to settled history,
// so "what happened to that payout?" and "is anything waiting?" are the
// same screen. A FAILED payout is reachable here rather than nowhere,
// which is what makes it reconcilable.
//
// Filter state lives in the URL so a triage view is shareable and
// survives a reload, matching /disputes and /ops/findings.
//
// ── what this page will not do ───────────────────────────────────────
//
// It never edits a destination. The address is frozen at request time
// server-side, and offering a field here would imply an approval means
// "send wherever it points now" rather than "send to what I reviewed".
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { CurrencyAmount, CurrencyBadge } from "@/components/ui/CurrencyAmount";
import { EmptyState } from "@/components/ui/EmptyState";
import { ErrorPanel } from "@/components/ui/ErrorPanel";
import { Input } from "@/components/ui/Input";
import { Pagination } from "@/components/ui/Pagination";
import { Select } from "@/components/ui/Select";
import { Skeleton } from "@/components/ui/Skeleton";
import { TechHint } from "@/components/ui/TechHint";
import { PayoutReviewDetail } from "@/components/treasury/PayoutReviewDetail";
import {
  PAYOUT_CURRENCY_HINT,
  PayoutCurrencyFilter,
} from "@/components/treasury/PayoutCurrencyFilter";
import { PayoutCurrencyPanel } from "@/components/treasury/PayoutCurrencyPanel";
import {
  isPayoutCurrency,
  parsePayoutCurrency,
  payoutCurrencies,
  payoutRail,
} from "@/components/treasury/payoutCurrency";
import {
  isDecidable,
  isDispatchable,
  payoutStatusHint,
  payoutStatusLabel,
  payoutStatusTone,
} from "@/components/treasury/payoutStatus";
import { truncMiddle } from "@/components/wallet-ops/orders";
import { useAdminDirectory, useAdminOptions } from "@/hooks/useAdminNames";
import { useMyPerms } from "@/hooks/useMyPerms";
import { cn } from "@/lib/cn";
import { fmtRelative } from "@/lib/format";
import type { CurrencyCode } from "@/lib/currency";
import { Perm } from "@/lib/perms";
import { translatePageCopy } from "@/i18n";
import { formatTokenAmount, tokenBaseFromSparkMinor } from "@/lib/tokenFmt";
import {
  ADMIN_WITHDRAWAL_STATUSES,
  fetchMyPermissions,
  fetchMyWithdrawalOptions,
  fetchWithdrawalQueue,
  keys,
  payoutKeys,
  type AdminWithdrawal,
  type AdminWithdrawalStatus,
  type WithdrawalOptionAsset,
} from "@/lib/queries";

const PAGE_SIZE = 25;

interface Search {
  /** CSV in canonical order; absent = the open-holds default. */
  status?: string;
  requester?: string;
  asset_id?: string;
  network_id?: string;
  /**
   * Settlement currency, applied in the browser (SM-499).
   *
   * The queue endpoint has no currency parameter and the rows carry no
   * currency column, so this narrows what the page already holds rather
   * than what it asks for. Kept in the URL like every other filter so a
   * triage view stays shareable.
   *
   * Typed as the code union rather than `string` because that is what
   * `parsePayoutCurrency` returns and what every consumer here expects.
   *
   * It was originally forced: `/treasury` used to declare `?currency=`
   * on the LAYOUT route, so every page under it inherited the param and
   * could only narrow the inherited type. That is fixed — the wallets
   * list owns its param from `treasury.index.tsx` now — and this stays
   * a union because it should have been one anyway.
   */
  currency?: CurrencyCode;
  /** Plain YYYY-MM-DD as typed; converted to a UTC instant on the wire. */
  from?: string;
  to?: string;
  page?: number;
  /** The request whose detail panel is open, so a decision is linkable. */
  open?: string;
}

/**
 * Parse + canonicalise the status filter. Unknown values are dropped
 * and the order is re-derived from the lifecycle, so a hand-edited
 * ?status= cannot produce a second cache entry for the same result set.
 */
function parseStatuses(v: unknown): AdminWithdrawalStatus[] {
  if (typeof v !== "string" || !v) return [];
  const wanted = new Set(v.split(",").map((s) => s.trim().toUpperCase()));
  return ADMIN_WITHDRAWAL_STATUSES.filter((s) => wanted.has(s));
}

const toCsv = (s: AdminWithdrawalStatus[]): string | undefined =>
  s.length > 0 ? s.join(",") : undefined;

const str = (v: unknown): string | undefined =>
  typeof v === "string" && v ? v : undefined;

// A date box says "2026-08-04" and means a whole day. The server bounds
// created_at half-open, so a day is [that date 00:00Z, the next 00:00Z)
// — computed here rather than sending a naive end-of-day, which would
// silently drop the last second of the range.
function dayStart(d?: string): string | undefined {
  return d ? `${d}T00:00:00Z` : undefined;
}
function dayAfter(d?: string): string | undefined {
  if (!d) return undefined;
  const t = Date.parse(`${d}T00:00:00Z`);
  if (Number.isNaN(t)) return undefined;
  return new Date(t + 86_400_000).toISOString().replace(/\.\d+Z$/, "Z");
}

export const Route = createFileRoute("/_app/treasury/payouts")({
  validateSearch: (s: Record<string, unknown>): Search => ({
    status: toCsv(parseStatuses(s.status)),
    requester: str(s.requester),
    asset_id: str(s.asset_id),
    network_id: str(s.network_id),
    currency: parsePayoutCurrency(s.currency),
    from: str(s.from),
    to: str(s.to),
    page:
      Number.isFinite(Number(s.page)) && Number(s.page) > 0 ? Number(s.page) : undefined,
    open: str(s.open),
  }),
  component: PayoutQueuePage,
});

function PayoutQueuePage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const perms = useMyPerms();

  const selected = parseStatuses(search.status);
  const page = search.page ?? 0;

  const filter = {
    status: search.status,
    requester: search.requester,
    asset_id: search.asset_id,
    network_id: search.network_id,
    date_from: dayStart(search.from),
    date_to: dayAfter(search.to),
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  };

  const q = useQuery({
    queryKey: payoutKeys.queue(filter),
    queryFn: () => fetchWithdrawalQueue(filter),
    // A payout waiting on a decision is somebody's money sitting in
    // escrow; the queue refreshes on its own so a reviewer with the tab
    // open does not act on a request a colleague already took.
    refetchInterval: 15_000,
    enabled: perms.has(Perm.AdminPayoutReview),
  });

  // The acting admin's own id, for the self-approval guard. Same query
  // key as useMyPerms, so this is the response that hook already has.
  const meQ = useQuery({
    queryKey: keys.myPerms(),
    queryFn: fetchMyPermissions,
    staleTime: 60_000,
  });

  // The crypto catalog, for the asset/network filters and for pricing a
  // row that the rail has not sent yet. /me/withdrawal-options is the
  // ungated projection — a reviewer holding only ADMIN_PAYOUT_REVIEW
  // does not also hold CRYPTO_WALLET_VIEW, and should not need to.
  //
  // It lists what is CURRENTLY withdrawable, so a historical payout on
  // a since-disabled asset has no filter entry. The rows still name it:
  // the server resolves each row's own asset and network from the full
  // catalog, archived ones included.
  const optionsQ = useQuery({
    queryKey: payoutKeys.options(),
    queryFn: fetchMyWithdrawalOptions,
    staleTime: 5 * 60_000,
  });
  const assetByID = useMemo(() => {
    const m = new Map<string, WithdrawalOptionAsset>();
    for (const a of optionsQ.data?.assets ?? []) m.set(a.asset_id, a);
    return m;
  }, [optionsQ.data]);

  const rows = q.data?.withdrawals ?? [];

  // The settlement-currency narrowing (SM-499). Applied here rather than
  // upstream because the queue endpoint has no currency parameter — see
  // the Search type — so this is a view over the page that was fetched,
  // and the counters below say so whenever it actually removes a row.
  const currency = parsePayoutCurrency(search.currency);
  const visible = useMemo(
    () =>
      currency
        ? rows.filter((wd) => payoutCurrencies(wd).settlement === currency)
        : rows,
    [currency, rows],
  );
  const currencyNarrowed = currency !== undefined && visible.length !== rows.length;

  // Asking for a currency the rail cannot settle in is a fair question
  // with a policy answer, and the empty list is where it gets answered.
  // Without this the operator reads "no Green payouts yet" and waits for
  // one — there will never be one, and the reason is not a defect.
  const unsettleableHint = useMemo(() => {
    if (!currency || isPayoutCurrency(currency)) return undefined;
    const rail = payoutRail(currency);
    return rail.kind === "convert"
      ? `No payout settles in ${currency}. It has no rail of its own — convert it along ${rail.label} first, and the payout that follows settles in ${rail.to}.`
      : `No payout settles in ${currency}. Nothing converts out of it and it can never be withdrawn, so no payout is ever denominated in it.`;
  }, [currency]);

  // Who the "Any requester" filter can offer.
  //
  // The roster is the good answer — every admin, named and qualified
  // when two share a display name — but it needs ADMIN_VIEW, and the
  // reviewer this page was built for holds ADMIN_PAYOUT_REVIEW and
  // nothing else. For them useAdminOptions() degrades to a list of one:
  // themselves, seeded from /me/permissions. A picker offering only you
  // is worse than no picker, because it reads as "you are the only
  // person who has ever requested a payout".
  //
  // So when the directory is shut, the requesters ON the rows stand in.
  // /admin/admin-withdrawals resolves admin_name server-side, so these
  // are real names, not ids — just narrowed to who is currently listed
  // rather than everyone who could be.
  //
  // A shared display_name still has to be qualified (SM-358): SIT alone
  // carries five admins called "Bootstrap admin", and two picker entries
  // reading the same is the exact failure the roster's collision pass
  // exists to prevent. Only the id is unique here — the rows carry no
  // email — and it is truncated in the MIDDLE because a ULID's leading
  // characters are a timestamp: two admins seeded seconds apart differ
  // in the tail, which a trailing ellipsis would be hiding.
  const directory = useAdminDirectory();
  const roster = useAdminOptions();
  const requesters = useMemo(() => {
    if (directory.available) return roster;
    const named = new Map<string, string>();
    for (const wd of rows) {
      if (!wd.admin_id || named.has(wd.admin_id)) continue;
      named.set(wd.admin_id, wd.admin_name?.trim() || wd.admin_id);
    }
    // A requester already selected stays offered even when this page of
    // rows does not contain them — a filter that is applied while the
    // control reads "Any requester" is the one wrong state here.
    if (search.requester && !named.has(search.requester)) {
      named.set(
        search.requester,
        directory.labels.get(search.requester) ?? search.requester,
      );
    }
    const shared = new Set<string>();
    const seen = new Set<string>();
    for (const name of named.values()) {
      if (seen.has(name)) shared.add(name);
      seen.add(name);
    }
    return [...named]
      .map(([id, name]) => ({
        id,
        name: shared.has(name) ? `${name} (${truncMiddle(id, 8)})` : name,
      }))
      .sort((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id));
  }, [directory.available, directory.labels, roster, rows, search.requester]);

  const total = q.data?.page?.total ?? 0;
  const filtered =
    selected.length > 0 ||
    Boolean(
      search.requester ||
        search.asset_id ||
        search.network_id ||
        currency ||
        search.from ||
        search.to,
    );

  // Any change to what is selected resets to page 1 — the row that was
  // on page 2 of the old result set is somewhere else in the new one,
  // and an out-of-range offset renders an empty page over real rows.
  function apply(next: Partial<Search>) {
    void navigate({ search: (s) => ({ ...s, ...next, page: undefined }) });
  }

  if (perms.loaded && !perms.has(Perm.AdminPayoutReview)) {
    return (
      <EmptyState
        title="No access"
        description="You need the ADMIN_PAYOUT_REVIEW permission to review admin payouts. Your own requests are on My balance."
      />
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl tracking-tight">Admin payouts</h1>
        <p className="text-sm text-muted">
          Withdrawal requests from admins' own commission balances. The amount is
          held in escrow from the moment it is requested — approving releases it to
          the address on the request, rejecting returns it.
        </p>
      </div>

      <PayoutCurrencyPanel />

      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-3">
          <StatusFilterMenu
            selected={selected}
            onChange={(next) => apply({ status: toCsv(next) })}
          />
          {/* Hidden rather than empty when there is nobody to offer: a
              reviewer without the roster on a page whose rows name no one
              would otherwise get a control that only ever says "Any
              requester". */}
          {requesters.length > 0 && (
            <Select
              aria-label="Requester"
              placeholder="Any requester"
              value={search.requester ?? ""}
              onChange={(e) => apply({ requester: e.target.value || undefined })}
              options={requesters.map((a) => ({ value: a.id, label: a.name }))}
              className="min-w-[180px]"
            />
          )}
          <Select
            aria-label="Network"
            placeholder="Any network"
            value={search.network_id ?? ""}
            onChange={(e) =>
              // Changing the network drops an asset that no longer belongs
              // to it, so the pair can never describe nothing.
              apply({
                network_id: e.target.value || undefined,
                asset_id:
                  search.asset_id &&
                  assetByID.get(search.asset_id)?.network_id === e.target.value
                    ? search.asset_id
                    : undefined,
              })
            }
            options={(optionsQ.data?.networks ?? []).map((n) => ({
              value: n.network_id,
              label: n.name || n.network_id,
            }))}
            className="min-w-[150px]"
          />
          <Select
            aria-label="Asset"
            placeholder="Any asset"
            value={search.asset_id ?? ""}
            onChange={(e) => apply({ asset_id: e.target.value || undefined })}
            options={(optionsQ.data?.assets ?? [])
              .filter((a) => !search.network_id || a.network_id === search.network_id)
              .map((a) => ({ value: a.asset_id, label: a.symbol || a.asset_id }))}
            className="min-w-[130px]"
          />
          <PayoutCurrencyFilter
            value={currency}
            onChange={(next) => apply({ currency: next })}
            className="min-w-[170px]"
          />
          <DateRange
            from={search.from}
            to={search.to}
            onChange={(next) => apply(next)}
          />
          {filtered && (
            <Button variant="ghost" onClick={() => void navigate({ search: () => ({}) })}>
              Reset
            </Button>
          )}
        </div>

        {/* Its own line under the bar rather than the picker's hint: the
            filters are bottom-aligned, and a two-line control drags every
            neighbour down with it. */}
        <p className="text-2xs text-muted">{PAYOUT_CURRENCY_HINT}</p>
        </div>

      <Card>
        <CardHeader className="gap-3 border-b border-border">
          <CardTitle>
            {selected.length === 0
              ? "Awaiting review and in flight"
              : selected.map(payoutStatusLabel).join(" · ")}
          </CardTitle>
          {/* The count follows whichever list is on screen. A currency
              filter narrows in the browser, so the server's total stops
              describing what is below it the moment it removes a row —
              and a badge that keeps claiming the wider number reads as
              rows that failed to render. */}
          <span className="text-2xs text-muted tabular-nums">
            {currencyNarrowed ? visible.length : total}
          </span>
          {currencyNarrowed && (
            <span className="text-2xs text-muted">counted on this page</span>
          )}
          {selected.length === 0 && (
            <span className="text-2xs text-muted">
              settled requests are under the status filter
            </span>
          )}
        </CardHeader>
        <CardBody className="p-0">
          {q.isLoading && (
            <div className="space-y-2 p-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-14 rounded-lg" />
              ))}
            </div>
          )}
          {q.isError && <ErrorPanel error={q.error} reset={() => void q.refetch()} />}
          {!q.isLoading && !q.isError && visible.length === 0 && (
            <EmptyState
              title="Nothing to review"
              description={
                unsettleableHint ??
                (filtered
                  ? "No payout requests match this filter. Clear it to see everything still awaiting review."
                  : "No admin has a payout waiting. Settled requests are under the status filter.")
              }
            />
          )}
          {visible.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-surface-2">
                  <tr>
                    <th className={cn(TH, "text-left")}>Requested</th>
                    <th className={cn(TH, "text-left")}>Requester</th>
                    {/* Source and settlement, as their own column rather
                        than a mark tucked beside the figure: what a
                        payout is denominated in is a fact a reviewer
                        scans down, and it is the fact this queue used to
                        leave entirely to inference. */}
                    <th className={cn(TH, "text-left")}>Currency</th>
                    <th className={cn(TH, "text-left")}>Asset</th>
                    <th className={cn(TH, "text-right")}>Amount</th>
                    <th className={cn(TH, "text-left")}>Destination</th>
                    <th className={cn(TH, "text-left")}>State</th>
                    <th className={cn(TH, "text-right")} />
                  </tr>
                </thead>
                <tbody>
                  {visible.map((wd) => (
                    <QueueRow
                      key={wd.withdrawal_id}
                      wd={wd}
                      assetByID={assetByID}
                      onOpen={() =>
                        void navigate({
                          search: (s) => ({ ...s, open: wd.withdrawal_id }),
                        })
                      }
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Hidden while a browser-side currency filter is actually
          removing rows: the offsets the pager would send describe the
          unfiltered queue, so page 2 of a narrowed list is not a list
          the server can return. The status and requester filters are
          upstream and keep their pager. */}
      <Pagination
        className={cn("justify-center", currencyNarrowed && "hidden")}
        page={{
          limit: PAGE_SIZE,
          offset: page * PAGE_SIZE,
          total,
          has_more: (page + 1) * PAGE_SIZE < total,
        }}
        currentPage={page}
        onPageChange={(next) =>
          void navigate({ search: (s) => ({ ...s, page: next || undefined }) })
        }
        itemLabel="requests"
      />

      {search.open && (
        <PayoutReviewDetail
          withdrawalID={search.open}
          fallback={rows.find((r) => r.withdrawal_id === search.open)}
          myAdminID={meQ.data?.admin_id}
          assetByID={assetByID}
          onClose={() =>
            void navigate({ search: (s) => ({ ...s, open: undefined }) })
          }
        />
      )}
    </div>
  );
}

const TH = "px-3 py-3 text-2xs font-medium uppercase tracking-wide text-muted";

function QueueRow({
  wd,
  assetByID,
  onOpen,
}: {
  wd: AdminWithdrawal;
  assetByID: Map<string, WithdrawalOptionAsset>;
  onOpen: () => void;
}) {
  const asset = assetByID.get(wd.asset_id);
  const decimals = wd.asset_decimals ?? asset?.token_decimals;
  const symbol = wd.asset_symbol || asset?.symbol;
  const { source, settlement } = payoutCurrencies(wd);
  // Exact beats estimate: once the rail has priced the send the row is
  // a receipt, and today's rate would misreport what actually left.
  const sent =
    wd.token_amount_base && decimals !== undefined
      ? formatTokenAmount(wd.token_amount_base, decimals, symbol)
      : null;
  const estimate =
    sent === null ? tokenBaseFromSparkMinor(wd.amount, asset, decimals) : null;

  return (
    <tr className="border-b border-border/60 transition-colors last:border-0 hover:bg-surface-2">
      <td className="px-3 py-2.5 text-2xs text-muted">{fmtRelative(wd.created_at)}</td>
      <td className="max-w-[18ch] truncate px-3 py-2.5">
        {/* The NAME, with the id one hover away. Approving a payout is a
            judgement about a person; a ULID does not support one. */}
        <TechHint tech={wd.admin_id}>{wd.admin_name || wd.admin_id}</TechHint>
      </td>
      <td className="px-3 py-2.5">
        {/* Source and settlement are shown together and only ONCE when
            they agree. Two identical chips with an arrow between them
            would suggest a conversion happened, which is a claim about
            money that has to be true to be made. */}
        <span className="flex items-center gap-1">
          <CurrencyBadge currency={source} />
          {source !== settlement ? (
            <>
              <span aria-hidden="true" className="text-2xs text-muted">
                →
              </span>
              <CurrencyBadge currency={settlement} />
            </>
          ) : null}
        </span>
      </td>
      <td className="px-3 py-2.5">
        <span className="flex flex-col gap-0.5 leading-tight">
          <span className="font-medium">{symbol || wd.asset_id || "—"}</span>
          <span className="text-2xs text-muted">
            {wd.network_name || wd.network_id || "—"}
          </span>
        </span>
      </td>
      <td className="px-3 py-2.5 text-right">
        <span className="flex flex-col items-end gap-0.5 leading-tight">
          {/* The currency's own mark and display precision, from the
              operator's registry — not the legacy single-currency
              renderer, which draws every figure as though it were the
              one currency that existed before this epic. */}
          <CurrencyAmount currency={settlement} value={wd.amount} />
          {sent ? (
            <span className="font-mono text-2xs text-muted">{sent}</span>
          ) : estimate !== null ? (
            <span
              className="font-mono text-2xs text-muted"
              title="Converted at the asset's current rate — this payout has not been priced on-chain yet."
            >
              ≈ {formatTokenAmount(estimate.toString(), decimals, symbol)}
            </span>
          ) : null}
        </span>
      </td>
      <td className="px-3 py-2.5 font-mono text-2xs">
        <TechHint tech={wd.destination_address}>
          {truncMiddle(wd.destination_address, 8)}
        </TechHint>
      </td>
      <td className="px-3 py-2.5">
        <Badge tone={payoutStatusTone(wd.status)}>{payoutStatusLabel(wd.status)}</Badge>
      </td>
      <td className="px-3 py-2.5 text-right">
        {/* Every row opens, not just the actionable ones: a settled
            payout's detail is how a reviewer answers what happened to
            it, which is half of what the status filter is for.
            APPROVED gets the loud variant too — it is approved money
            that never went out, and it needs someone to act. */}
        <Button
          size="sm"
          variant={isDecidable(wd) || isDispatchable(wd) ? "primary" : "ghost"}
          onClick={onOpen}
        >
          {isDecidable(wd) ? "Review" : isDispatchable(wd) ? "Send" : "Open"}
        </Button>
      </td>
    </tr>
  );
}

// The two date boxes. Applied on change rather than on a submit button:
// a filter the operator believes is applied while the list still shows
// everything is the failure mode worth avoiding.
function DateRange({
  from,
  to,
  onChange,
}: {
  from?: string;
  to?: string;
  onChange: (next: { from?: string; to?: string }) => void;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <Input
        type="date"
        aria-label="Requested from"
        value={from ?? ""}
        max={to}
        onChange={(e) => onChange({ from: e.target.value || undefined })}
        className="w-[9.5rem]"
      />
      <span className="text-2xs text-muted">→</span>
      <Input
        type="date"
        aria-label="Requested to"
        value={to ?? ""}
        min={from}
        onChange={(e) => onChange({ to: e.target.value || undefined })}
        className="w-[9.5rem]"
      />
    </div>
  );
}

/**
 * The status filter: a button opening a checkbox list, so several
 * states can be selected at once and "the default" is one click back.
 *
 * Native checkboxes rather than a custom listbox (the shared Select is
 * single-select) — they carry keyboard operation, focus order and the
 * checked state to assistive tech for free. Same construction as the
 * dispute queue's filter, deliberately: two triage screens that behave
 * differently for the same job is its own cost.
 */
function StatusFilterMenu({
  selected,
  onChange,
}: {
  selected: AdminWithdrawalStatus[];
  onChange: (next: AdminWithdrawalStatus[]) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const toggle = (v: AdminWithdrawalStatus) =>
    onChange(
      selected.includes(v)
        ? selected.filter((x) => x !== v)
        : // Re-derive from lifecycle order rather than appending, so the
          // URL does not depend on click order.
          ADMIN_WITHDRAWAL_STATUSES.filter((x) => x === v || selected.includes(x)),
    );

  const summary =
    selected.length === 0
      ? t("common.filterSummary.openHolds")
      : selected.length === 1
        ? translatePageCopy(payoutStatusLabel(selected[0]))
        : t("common.filterSummary.stateCount", { count: selected.length });

  return (
    <div className="relative" ref={rootRef}>
      <Button
        ref={triggerRef}
        variant="secondary"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="true"
        aria-label={t("common.filterSummary.byState", { summary })}
      >
        <span className="text-2xs uppercase tracking-wide text-muted">State</span>
        <span>{summary}</span>
        <svg
          aria-hidden="true"
          width="12"
          height="12"
          viewBox="0 0 12 12"
          className={cn("transition-transform", open && "rotate-180")}
        >
          <path
            d="M3 4.5l3 3 3-3"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </svg>
      </Button>

      {open && (
        <div
          role="group"
          aria-label="Filter payouts by state"
          className="absolute left-0 z-30 mt-1 w-80 rounded-lg border border-border bg-surface p-1 shadow-card"
        >
          <button
            type="button"
            onClick={() => onChange([])}
            className="flex w-full items-start justify-between gap-2 rounded-md px-3 py-2 text-left text-sm text-fg hover:bg-surface-2"
          >
            <span>
              Open holds
              <span className="mt-1 block text-2xs text-muted">
                Everything still holding money — the work to do.
              </span>
            </span>
            {selected.length === 0 && <span aria-hidden="true">✓</span>}
          </button>
          <div className="my-1 border-t border-border" />
          {ADMIN_WITHDRAWAL_STATUSES.map((v) => (
            <label
              key={v}
              className="flex cursor-pointer items-start gap-3 rounded-md px-3 py-2 hover:bg-surface-2"
            >
              <input
                type="checkbox"
                className="mt-0.5 accent-neon"
                checked={selected.includes(v)}
                onChange={() => toggle(v)}
                aria-label={payoutStatusLabel(v)}
              />
              <span className="min-w-0">
                <Badge tone={payoutStatusTone(v)}>{payoutStatusLabel(v)}</Badge>
                <span className="mt-1 block text-2xs text-muted">
                  {payoutStatusHint[v]}
                </span>
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
